// React Query wiring for capabilities (step 6, "what to build").
//
// Generation runs on the streaming Lambda Function URL (like synthesis /
// assumptions): a forced-tool Bedrock call can exceed API Gateway's 30s, so it
// streams from streamUrl. The proposal is persisted server-side at
// CAPABILITY#LATEST; `useCapabilities` reads it, generation refreshes it, and
// promote/dismiss curate it. Promotion writes a `capability` graph node plus its
// mitigates/enables edges, so that mutation also invalidates the canvas + project
// queries.

import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useConfig } from '@/app/ConfigProvider';
import { useAuth } from '@/components/auth/AuthProvider';
import { consumeNdjsonStream } from '@/services/stream';
import { useStreamPreview } from '@/hooks/use-stream-preview';
import type { CapabilityCandidate, CapabilityProposal, PromoteCapabilityInput, StreamedCapability } from '@/types/api';

export function useCapabilities(projectId: string) {
  return useQuery<CapabilityProposal | null>({
    queryKey: ['capabilities', projectId],
    queryFn: async () => {
      const res = await api.getCapabilities(projectId);
      if (!res.success) throw new Error(res.message || 'Failed to load capabilities');
      return res.data ?? null;
    },
  });
}

export function useGenerateCapabilities() {
  const queryClient = useQueryClient();
  const config = useConfig();
  const { getStreamAuthHeaders } = useAuth();
  // NB the streamed JSON array key is `capabilities`; the complete payload
  // renames to `candidates` (server adds ids/status at persist).
  const preview = useStreamPreview<StreamedCapability>('capabilities');

  const mutation = useMutation<CapabilityCandidate[], Error, string>({
    // Keyed so other surfaces can observe in-flight runs (see
    // useCapabilitiesRunInFlight), whichever instance fired.
    mutationKey: ['generate-capabilities'],
    mutationFn: async (projectId: string) => {
      preview.reset();
      const data = await consumeNdjsonStream<{ candidates?: CapabilityCandidate[] }>(
        `${config.api.streamUrl}/ai/capabilities`,
        { projectId },
        getStreamAuthHeaders(),
        preview.onChunk,
      );
      return data.candidates ?? [];
    },
    // Returning the invalidations keeps the mutation pending until the fresh
    // proposal is in cache — no stale flash when previews hand off.
    onSuccess: (_data, projectId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['capabilities', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      ]),
  });

  return {
    ...mutation,
    streamedCandidates: preview.items,
    streamStarted: preview.started,
    draftingTitle: preview.draftingTitle,
  };
}

// Whether ANY useGenerateCapabilities instance has a run in flight for this
// project (mutation state is per-instance; this crosses surfaces).
export function useCapabilitiesRunInFlight(projectId: string) {
  const pending = useMutationState({
    filters: { mutationKey: ['generate-capabilities'], status: 'pending' },
    select: (m) => m.state.variables as string,
  });
  return pending.includes(projectId);
}

export function usePromoteCapability(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PromoteCapabilityInput>({
    mutationFn: async (payload) => {
      const res = await api.promoteCapability(projectId, payload);
      if (!res.success) throw new Error(res.message || 'Failed to promote');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities', projectId] });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useDismissCapability(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (candidateId) => {
      const res = await api.dismissCapability(projectId, candidateId);
      if (!res.success) throw new Error(res.message || 'Failed to dismiss');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities', projectId] });
    },
  });
}
